import React, { useEffect, useState } from "react";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Bell, Plus, Trash2, ToggleLeft, ToggleRight, AlertTriangle,
  TrendingUp, Activity, Shield, Zap, AlertCircle
} from "lucide-react";

const RULE_TYPES = [
  {
    id: "error_rate_spike",
    label: "Error Rate Spike",
    icon: AlertTriangle,
    color: "text-red-500",
    bg: "bg-red-50",
    description: "Alert when error rate exceeds threshold % of total calls in window",
    fields: ["threshold", "window_minutes"],
    thresholdLabel: "Threshold (%)",
    thresholdDefault: 25,
  },
  {
    id: "volume_spike",
    label: "Volume Spike",
    icon: TrendingUp,
    color: "text-amber-500",
    bg: "bg-amber-50",
    description: "Alert when document volume exceeds threshold count in window",
    fields: ["threshold", "window_minutes"],
    thresholdLabel: "Max Documents",
    thresholdDefault: 100,
  },
  {
    id: "accuracy_drop",
    label: "Accuracy Drop",
    icon: Activity,
    color: "text-blue-500",
    bg: "bg-blue-50",
    description: "Alert when rejection rate exceeds threshold % of reviewed documents",
    fields: ["threshold", "window_minutes"],
    thresholdLabel: "Rejection Rate (%)",
    thresholdDefault: 20,
  },
  {
    id: "new_ip_source",
    label: "New IP Source",
    icon: Shield,
    color: "text-purple-500",
    bg: "bg-purple-50",
    description: "Alert when a new IP address submits documents",
    fields: [],
    thresholdLabel: null,
    thresholdDefault: null,
  },
  {
    id: "credit_exhaustion",
    label: "Credit Exhaustion",
    icon: Zap,
    color: "text-[#006080]",
    bg: "bg-[#E5F0F2]",
    description: "Alert at configured usage thresholds for page or AI credits",
    fields: ["credit_type", "preset_thresholds", "custom_threshold"],
    thresholdLabel: null,
    thresholdDefault: null,
  },
];

const PRESET_THRESHOLDS = [50, 75, 90, 100];

function NewRuleModal({ onClose, onSave, companies, isSuperAdmin }) {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [form, setForm] = useState({
    threshold: 25,
    window_minutes: 60,
    extra_recipients: "",
    credit_type: "page_credits",
    preset_thresholds: [75, 90],
    custom_threshold: "",
    company_id: isSuperAdmin ? "" : undefined,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const typeDef = RULE_TYPES.find(t => t.id === selectedType);

  const togglePreset = (val) => {
    setForm(f => ({
      ...f,
      preset_thresholds: f.preset_thresholds.includes(val)
        ? f.preset_thresholds.filter(v => v !== val)
        : [...f.preset_thresholds, val],
    }));
  };

  const handleSave = async () => {
    if (!selectedType) return;
    if (isSuperAdmin && !form.company_id) {
      setError("Please select a company or choose Global (All Companies)");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        rule_type: selectedType,
        is_active: true,
        extra_recipients: form.extra_recipients.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
      };
      if (isSuperAdmin) payload.company_id = form.company_id;
      if (typeDef.fields.includes("threshold")) payload.threshold = parseFloat(form.threshold) || 25;
      if (typeDef.fields.includes("window_minutes")) payload.window_minutes = parseInt(form.window_minutes) || 60;
      if (typeDef.fields.includes("credit_type")) payload.credit_type = form.credit_type;
      if (typeDef.fields.includes("preset_thresholds")) payload.preset_thresholds = form.preset_thresholds;
      if (typeDef.fields.includes("custom_threshold") && form.custom_threshold) {
        payload.custom_threshold = parseFloat(form.custom_threshold);
      }
      await api.post("/alerts/rules", payload);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create rule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#E5F0F2] flex items-center justify-center">
            <Bell size={14} className="text-[#006080]" />
          </div>
          <h3 className="font-bold text-slate-900">New Anomaly Rule</h3>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle size={13} /> {error}
            </div>
          )}

          {/* Company Selector — Superadmin only */}
          {isSuperAdmin && (
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1.5">
                Applies To <span className="text-red-500">*</span>
              </label>
              <select
                data-testid="alert-company-select"
                value={form.company_id}
                onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
              >
                <option value="">Select scope…</option>
                <option value="global">🌐 Global (All Companies)</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {form.company_id === "global" && (
                <p className="text-[10px] text-amber-600 mt-1">Global rules fire for every active company.</p>
              )}
            </div>
          )}

          {/* Rule Type Selector */}
          <div className="grid grid-cols-2 gap-2">
            {RULE_TYPES.map(rt => {
              const Icon = rt.icon;
              return (
                <button
                  key={rt.id}
                  onClick={() => setSelectedType(rt.id)}
                  data-testid={`rule-type-${rt.id}`}
                  className={`flex items-center gap-2.5 p-3 rounded-lg border text-left transition-all ${
                    selectedType === rt.id
                      ? "border-[#006080] bg-[#E5F0F2]"
                      : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <div className={`w-7 h-7 rounded-md ${rt.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon size={13} className={rt.color} />
                  </div>
                  <span className="text-xs font-semibold text-slate-700">{rt.label}</span>
                </button>
              );
            })}
          </div>

          {/* Rule Description */}
          {typeDef && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500 flex items-start gap-2">
              <AlertCircle size={12} className="mt-0.5 flex-shrink-0 text-[#006080]" />
              {typeDef.description}
            </div>
          )}

          {/* Rule Config */}
          {typeDef && (
            <div className="space-y-4">
              {typeDef.fields.includes("threshold") && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1.5">{typeDef.thresholdLabel}</label>
                    <input
                      type="number" min={1}
                      value={form.threshold}
                      onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))}
                      className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1.5">Time Window (minutes)</label>
                    <input
                      type="number" min={5}
                      value={form.window_minutes}
                      onChange={e => setForm(f => ({ ...f, window_minutes: e.target.value }))}
                      className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
                    />
                  </div>
                </div>
              )}

              {typeDef.fields.includes("credit_type") && (
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1.5">Credit Type</label>
                  <div className="flex gap-2">
                    {[["page_credits", "Page Credits"], ["ai_credits", "AI Credits"]].map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, credit_type: val }))}
                        className={`flex-1 h-9 text-sm font-medium rounded-md border transition-colors ${
                          form.credit_type === val
                            ? "bg-[#006080] text-white border-[#006080]"
                            : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {typeDef.fields.includes("preset_thresholds") && (
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1.5">Alert Thresholds</label>
                  <div className="flex gap-2 mb-3">
                    {PRESET_THRESHOLDS.map(p => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => togglePreset(p)}
                        data-testid={`preset-threshold-${p}`}
                        className={`flex-1 h-8 text-xs font-semibold rounded-md border transition-colors ${
                          form.preset_thresholds.includes(p)
                            ? "bg-[#006080] text-white border-[#006080]"
                            : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        {p}%
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Custom threshold (%)</label>
                    <input
                      type="number" min={1} max={100}
                      value={form.custom_threshold}
                      onChange={e => setForm(f => ({ ...f, custom_threshold: e.target.value }))}
                      placeholder="e.g. 85"
                      className="w-full h-8 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1.5">Alert Recipients (comma or newline separated)</label>
                <textarea
                  rows={2}
                  value={form.extra_recipients}
                  onChange={e => setForm(f => ({ ...f, extra_recipients: e.target.value }))}
                  placeholder="admin@company.com, ops@company.com"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">Company admin is always included. Add extra recipients here.</p>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
          <button onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50 transition-colors">Cancel</button>
          <button
            data-testid="save-rule-button"
            onClick={handleSave}
            disabled={!selectedType || saving}
            className="flex-1 h-9 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <><div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> Saving…</> : "Save Rule"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RuleCard({ rule, onDelete, onToggle, companies }) {
  const typeDef = RULE_TYPES.find(t => t.id === rule.rule_type);
  const Icon = typeDef?.icon || Bell;
  const companyName = rule.company_id === "global"
    ? "Global (All Companies)"
    : (companies.find(c => c.id === rule.company_id)?.name || rule.company_id?.slice(0, 8) || "—");

  const summary = [];
  if (rule.threshold && typeDef?.fields.includes("threshold")) summary.push(`Threshold: ${rule.threshold}${rule.rule_type === "volume_spike" ? " docs" : "%"}`);
  if (rule.window_minutes && typeDef?.fields.includes("window_minutes")) summary.push(`Window: ${rule.window_minutes}min`);
  if (rule.credit_type) summary.push(rule.credit_type === "page_credits" ? "Page Credits" : "AI Credits");
  if (rule.preset_thresholds?.length) summary.push(`Alerts at: ${rule.preset_thresholds.map(p => p + "%").join(", ")}`);

  return (
    <div
      data-testid={`alert-rule-${rule.id}`}
      className={`bg-white border rounded-lg p-4 transition-all ${rule.is_active ? "border-slate-200" : "border-slate-100 opacity-60"}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-md ${typeDef?.bg || "bg-slate-100"} flex items-center justify-center flex-shrink-0`}>
            <Icon size={14} className={typeDef?.color || "text-slate-500"} />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">{typeDef?.label || rule.rule_type}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">{summary.join(" · ") || "No config"}</p>
            {rule.company_id && (
              <p className="text-[10px] text-[#006080] font-medium mt-0.5">
                {rule.company_id === "global" ? "🌐 Global" : `Company: ${companyName}`}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onToggle(rule)}
            data-testid={`toggle-rule-${rule.id}`}
            className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-colors"
          >
            {rule.is_active ? <ToggleRight size={15} className="text-emerald-600" /> : <ToggleLeft size={15} />}
          </button>
          <button
            onClick={() => onDelete(rule.id)}
            data-testid={`delete-rule-${rule.id}`}
            className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-500 transition-colors"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      {rule.extra_recipients?.length > 0 && (
        <p className="text-[11px] text-slate-400 mt-2 pl-10">
          + {rule.extra_recipients.join(", ")}
        </p>
      )}
      {rule.last_triggered_at && (
        <p className="text-[10px] text-amber-600 mt-1 pl-10">
          Last triggered: {new Date(rule.last_triggered_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}

export default function AlertSettings() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const fetchRules = () => {
    const calls = [api.get("/alerts/rules")];
    if (isSuperAdmin) calls.push(api.get("/admin/companies"));
    Promise.all(calls).then(([r, c]) => {
      setRules(r.data);
      if (c) setCompanies(c.data.filter(co => co.is_active));
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchRules(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this alert rule?")) return;
    await api.delete(`/alerts/rules/${id}`);
    fetchRules();
  };

  const handleToggle = async (rule) => {
    await api.put(`/alerts/rules/${rule.id}`, { is_active: !rule.is_active });
    fetchRules();
  };

  const anomalyRules = rules.filter(r => r.rule_type !== "credit_exhaustion");
  const creditRules = rules.filter(r => r.rule_type === "credit_exhaustion");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Alert Settings</h2>
          <p className="text-sm text-slate-500 mt-0.5">Configure anomaly detection and credit exhaustion notifications.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="new-alert-rule-button"
          className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors"
        >
          <Plus size={14} /> New Rule
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-8">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Anomaly Rules</h3>
            {anomalyRules.length === 0 ? (
              <div className="text-center py-10 bg-white border border-slate-200 rounded-lg border-dashed">
                <AlertTriangle size={24} className="text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No anomaly rules configured</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {anomalyRules.map(r => (
                  <RuleCard key={r.id} rule={r} companies={companies} onDelete={handleDelete} onToggle={handleToggle} />
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Credit Exhaustion Rules</h3>
            {creditRules.length === 0 ? (
              <div className="text-center py-10 bg-white border border-slate-200 rounded-lg border-dashed">
                <Zap size={24} className="text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No credit exhaustion rules configured</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {creditRules.map(r => (
                  <RuleCard key={r.id} rule={r} companies={companies} onDelete={handleDelete} onToggle={handleToggle} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <NewRuleModal
          companies={companies}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchRules(); }}
        />
      )}
    </div>
  );
}
